import type { ComponentType, SVGProps } from 'react';
import type { I18nKey } from '../i18n/context.tsx';
import { useT } from '../i18n/index.ts';
import {
  ClaudeCodeLogo,
  CursorLogo,
  JetBrainsLogo,
  OpenClawLogo,
  OpenCodeLogo,
  VSCodeLogo,
} from './logos/index.tsx';

/**
 * Tools strip — DESIGN.md §6.5.
 *
 * `bg-butter/55` band with a subtle paper-grain overlay, centered
 * headline, and a responsive grid of six tool tiles. Each tile shows
 * a 26px inline brand logo SVG (from `logos/index.tsx`) plus the
 * tool name from i18n.
 *
 * The list of tools is data-driven. To add one: add a new entry to
 * `TOOLS`, add a matching `tools.{key}` i18n string, and add the
 * brand logo component. Tools without a public brand mark go into
 * the "also supports" footnote line rather than into the grid (per
 * DESIGN.md §7).
 */

interface ToolTile {
  readonly key: string;
  readonly labelKey: I18nKey;
  readonly Logo: ComponentType<SVGProps<SVGSVGElement>>;
  /** Optional Tailwind class override for the icon colour. */
  readonly iconClass?: string;
}

const TOOLS: readonly ToolTile[] = [
  { key: 'claude_code', labelKey: 'tools.claude_code', Logo: ClaudeCodeLogo },
  { key: 'cursor', labelKey: 'tools.cursor', Logo: CursorLogo, iconClass: 'text-ink' },
  { key: 'vscode', labelKey: 'tools.vscode', Logo: VSCodeLogo },
  { key: 'opencode', labelKey: 'tools.opencode', Logo: OpenCodeLogo },
  { key: 'jetbrains', labelKey: 'tools.jetbrains', Logo: JetBrainsLogo, iconClass: 'text-ink' },
  { key: 'openclaw', labelKey: 'tools.openclaw', Logo: OpenClawLogo },
];

export function Tools(): JSX.Element {
  const t = useT();
  return (
    <section id="tools" className="grain relative bg-butter/55 py-28" data-testid="tools">
      <div className="relative mx-auto max-w-7xl px-6">
        <div className="mb-14 text-center">
          <p className="mb-4 font-mono text-[11px] font-medium uppercase tracking-[0.22em] text-claude">
            {t('tools.eyebrow')}
          </p>
          <h2 className="text-[40px] font-semibold leading-[1.08] text-ink md:text-[52px]">
            {t('tools.title.lead')}
            <span className="serif text-claude">{t('tools.title.accent')}</span>
            {t('tools.title.trail')}
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-[16px] text-ink-muted">{t('tools.subtitle')}</p>
        </div>

        <div className="mx-auto grid max-w-5xl grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-6">
          {TOOLS.map(({ key, labelKey, Logo, iconClass }) => (
            <a
              key={key}
              href="#"
              className="lift ghost flex flex-col items-center gap-3 rounded-2xl bg-linen px-4 py-6 hover:shadow-warm-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-claude focus-visible:ring-offset-2 focus-visible:ring-offset-butter"
              data-testid={`tool-${key}`}
            >
              <Logo className={['h-[26px] w-[26px]', iconClass ?? ''].join(' ').trim()} />
              <span className="text-[12.5px] font-medium text-ink">{t(labelKey)}</span>
            </a>
          ))}
        </div>

        <p className="mt-10 text-center font-mono text-[13px] text-ink-subtle">
          {t('tools.footnote')}
        </p>
      </div>
    </section>
  );
}
